import { useCallback, useMemo } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import { User } from "features/User/models";
import { camelCase, isEmpty, pick } from "lodash";
import { setNewUserInQueryClient } from "features/User/hooks";
import useAutoSave from "components/FormElements/useAutoSave";
import { apiUserPathById } from "helpers/url/pages/v1";
import { useDeepCompareMemoize } from "use-deep-compare-effect";
import { UserUpdateFormValues, userUpdateSchema, values } from "./formDefinition";

function transformData(data: UserUpdateFormValues) {
  // will only pick fields that have a value
  const newHireJourney = pick(data, [
    "startDate",
    "peopleTeamContactId",
    "buddyId",
    "onboardingPlanUrl",
    "userId",
  ]);

  const user = pick(data, [
    "firstName",
    "lastName",
    "preferredName",
    "title",
    "pronouns",
    "personalEmail",
    "workEmail",
    "location",
    "timezone",
    "rawLocation",
    "googleLocationId",
    "managerId",
    "employmentType",
    "department",
    "workMode",
    "bio",
    "regionalHub",
    "atsProfileUrl",
    "country",
    "phoneNumber",
    "phoneCountryCode",
    "linkedinUrl",
  ]);

  return {
    ...(isEmpty(user) ? {} : { user }),
    ...(isEmpty(newHireJourney) ? {} : { newHireJourney }),
  };
}

interface useUserAutoSaveFormProps {
  user?: User | undefined;
  userPrefix?: string;
  fields: Array<keyof UserUpdateFormValues>;
  onSuccess?: () => void;
  onClose?: () => void;
}

export default function useUserAutoSaveForm({
  user,
  fields,
  onSuccess = () => {},
  onClose = () => {},
}: useUserAutoSaveFormProps) {
  const camelFields = useMemo(() => fields.map((field) => camelCase(field)), [fields]);
  const queryClient = useQueryClient();
  const onUpdateUserSuccess = useCallback((resp: User) => {
    setNewUserInQueryClient(queryClient, resp);
    onSuccess?.();
  }, [onSuccess, queryClient]);

  const memoizedDeps = useDeepCompareMemoize([user, camelFields]);
  const defaultValues = useMemo(() => {
    if (!user) return {};
    return pick(values(user), camelFields);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memoizedDeps]);

  const form = useForm({
    mode: "onBlur",
    resolver: yupResolver(userUpdateSchema),
    values: defaultValues,
  });

  const { isSubmitting, isSuccess, isError } = useAutoSave({
    form,
    submitUrl: apiUserPathById(user?.id || ""),
    onSuccess: onUpdateUserSuccess,
    transformData,
  });
  return {
    form,
    isReady: !!user,
    isSubmitting,
    isSuccess,
    isError,
  };
}
